import type { CSSProperties, MouseEvent } from "react";
import { Link } from "react-router-dom";
import type { StudentCohortAssignmentReleased } from "../auth/api-client";
import { LessonIcon } from "../teacher/LessonIcon";
import { QuizIcon } from "../teacher/QuizIcon";
import { PresentationIcon } from "../teacher/PresentationIcon";
import { fallbackGradient, lessonIconDefinition } from "../teacher/lessonIcons";
import { textColorsForBackground } from "../teacher/lessonCardColors";
import { isAdvancedLessonMode, itemRowEpicFrameStyle, lessonEpicVisualStyle } from "../teacher/lessonAdvancedVisual";
import { buildCohortLessonStudentLink } from "./cohortLessonStudentLink";
import { formatDueCountdown, formatDueDateShort } from "./formatDueCountdown";
import {
  deriveStudentDisplayStatus,
  studentAssignmentOpen,
  studentDisplayStatusLabel,
} from "./studentDisplayStatus";
import { useLessonLaunch } from "../transition/LessonLaunchProvider";
import { quizStudentActionLabel } from "./quizStudentActionLabel";

function AssignmentOpenBadge({ a }: { a: StudentCohortAssignmentReleased }) {
  if (!a.released) {
    return <span className="student-lesson-availability student-lesson-availability--planned">Planned</span>;
  }
  if (a.closed) {
    return <span className="student-lesson-availability student-lesson-availability--closed">Closed</span>;
  }
  return <span className="student-lesson-availability student-lesson-availability--open">Open</span>;
}

function StudentStatusBadge({ a }: { a: StudentCohortAssignmentReleased }) {
  const open = studentAssignmentOpen(a);
  const status = deriveStudentDisplayStatus(a);
  const mod = open ? "open" : "closed";
  return (
    <span className={`student-display-status student-display-status--${mod} student-display-status--${status}`}>
      {studentDisplayStatusLabel(a)}
    </span>
  );
}

function DueMeta({ dueAt, nowMs, open }: { dueAt: string | null | undefined; nowMs: number; open: boolean }) {
  if (!dueAt) return <span className="student-cohort-details-lesson-muted">No due date</span>;
  if (open) {
    const countdown = formatDueCountdown(dueAt, nowMs);
    const past = countdown === "Past due";
    return (
      <span className={past ? "student-cohort-details-lesson-due--past" : undefined}>
        {formatDueDateShort(dueAt)}
        {countdown ? ` · ${countdown}` : ""}
      </span>
    );
  }
  return <span className="student-cohort-details-lesson-muted">{formatDueDateShort(dueAt)}</span>;
}

export interface StudentCohortLessonRowProps {
  readonly assignment: StudentCohortAssignmentReleased;
  readonly cohortId: string;
  readonly index: number;
  readonly nowMs: number;
}

export function StudentCohortLessonRow({ assignment: a, cohortId, index, nowMs }: StudentCohortLessonRowProps) {
  const launch = useLessonLaunch();
  const isQuiz = a.itemKind === "quiz" && a.quizId;
  const isPresentation = a.itemKind === "presentation" && a.presentationId;
  const itemKey = isQuiz
    ? a.quizId!
    : isPresentation
      ? a.presentationId!
      : a.lessonId ?? a.assignmentId;
  const quizUrl = `/quiz?cohortId=${encodeURIComponent(cohortId)}&quizId=${encodeURIComponent(a.quizId ?? "")}`;
  const presentationUrl = `/presentation?cohortId=${encodeURIComponent(cohortId)}&presentationId=${encodeURIComponent(a.presentationId ?? "")}`;
  const openUrl = isQuiz
    ? quizUrl
    : isPresentation
      ? presentationUrl
      : buildCohortLessonStudentLink({
          cohortId,
          lessonId: a.lessonId ?? "",
          lessonTitle: a.lessonTitle,
          iconName: a.iconName,
          iconBgColor: a.iconBgColor,
          iconFgColor: a.iconFgColor,
          workspaceMode: a.workspaceMode,
          reviewClosed: a.closed && a.released,
        });
  const canOpen = a.open;
  const canReviewClosed = !isQuiz && a.closed && a.released;
  const advanced = !isQuiz && !isPresentation && isAdvancedLessonMode(a.workspaceMode);
  const epic = lessonEpicVisualStyle({
    workspaceMode: a.workspaceMode,
    baseColor: a.iconBgColor,
    fallbackSeed: itemKey,
  });
  const cardStyle = (advanced ? itemRowEpicFrameStyle(a.iconBgColor, itemKey) : undefined) as
    | CSSProperties
    | undefined;
  const launchBackground = epic?.innerBackground ?? a.iconBgColor?.trim() ?? fallbackGradient(itemKey);
  const launchWatermarkColor = a.iconFgColor?.trim() || textColorsForBackground(launchBackground).title;
  const launchIcon = lessonIconDefinition(a.iconName);

  const onOpen = (e: MouseEvent<HTMLAnchorElement>) => {
    if (isQuiz || isPresentation) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    launch({ to: openUrl, background: launchBackground, watermarkColor: launchWatermarkColor, iconDef: launchIcon });
  };

  const cardClass = [
    "student-cohort-details-lesson-card",
    isQuiz
      ? "student-cohort-details-lesson-card--quiz"
      : isPresentation
        ? "student-cohort-details-lesson-card--quiz"
        : "student-cohort-details-lesson-card--lesson",
    advanced ? "student-cohort-details-lesson-card--advanced" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const quizTakeDisabled = Boolean(isQuiz && canOpen && a.progressStatus === "complete");
  const actionLabel = isQuiz ? quizStudentActionLabel(a) : "Open";

  return (
    <li className="student-cohort-details-lesson">
      <div className={cardClass} style={cardStyle}>
        <div className="student-cohort-details-lesson-body">
          <div className="student-cohort-details-lesson-num-col">
            <span className="student-cohort-details-kind-label">
              {isQuiz ? "Quiz" : isPresentation ? "Slides" : "Lesson"}
            </span>
            <span className="student-cohort-details-lesson-num" aria-hidden>
              {index + 1}
            </span>
          </div>
          {isQuiz ? (
            <QuizIcon
              quizId={a.quizId!}
              title={a.lessonTitle}
              iconName={a.iconName}
              bgColor={a.iconBgColor}
              fgColor={a.iconFgColor}
              size={3.75}
            />
          ) : isPresentation ? (
            <PresentationIcon
              title={a.lessonTitle}
              iconName={a.iconName}
              bgColor={a.iconBgColor}
              fgColor={a.iconFgColor}
              size={3.75}
            />
          ) : (
            <LessonIcon
              lessonId={a.lessonId ?? a.assignmentId}
              title={a.lessonTitle}
              iconName={a.iconName}
              bgColor={a.iconBgColor}
              fgColor={a.iconFgColor}
              workspaceMode={a.workspaceMode}
              size={3.75}
            />
          )}
          <div className="student-cohort-details-lesson-main">
            <h3 className="student-cohort-details-lesson-title">{a.lessonTitle}</h3>
            <div className="student-cohort-details-lesson-meta">
              <AssignmentOpenBadge a={a} />
              {a.released ? <StudentStatusBadge a={a} /> : null}
              {isQuiz && a.bestScorePercent != null ? (
                <span className="student-quiz-score-pill">Best: {a.bestScorePercent}%</span>
              ) : null}
              {a.released ? <DueMeta dueAt={a.dueAt} nowMs={nowMs} open={a.open} /> : null}
            </div>
          </div>
          <div className="student-cohort-details-lesson-action">
            {canOpen ? (
              quizTakeDisabled ? (
                <span className="btn primary tight" aria-disabled="true">
                  {actionLabel}
                </span>
              ) : (
                <Link
                  to={openUrl}
                  onClick={onOpen}
                  className="btn primary tight"
                  style={{ textDecoration: "none" }}
                >
                  {actionLabel}
                </Link>
              )
            ) : canReviewClosed ? (
              <Link to={openUrl} onClick={onOpen} className="btn secondary tight" style={{ textDecoration: "none" }}>
                Review
              </Link>
            ) : !a.released ? (
              <span className="student-cohort-details-lesson-muted small">Not assigned yet</span>
            ) : null}
          </div>
        </div>
      </div>
    </li>
  );
}
